import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ADPResourceService } from 'digital-core';
import { AppConstants } from 'src/app/core/constants';
import { LoggedInUserSettings } from 'src/app/core/services';
import { calendarDateOf, daysUntil } from 'src/app/core/utils/plan-coverage-days.util';
import { getMaxThresholdDays } from 'src/app/core/utils/renewal-notice-thresholds.util';
import { RenewalStatus } from 'src/app/model/addons/afx-renewal.dto';

export type PlanRenewalContext = 'Dashboard' | 'MyPlan' | 'Global';

export type RenewalNoticeIcon = 'warning' | 'information' | 'success';

// One entry of the {Context}_Renewal_Notice_Thresholds CMS label. Every state the notice can
// render — including "renewal accepted" — is one of these, so adding or retuning a state is a
// content change rather than a code change. An entry is matched either on `days` (how much
// coverage is left) or on `status` (afxRenewal.renewalStatus); see matchedState.
export interface RenewalNoticeStateDescriptor {
  days?: number;
  // Comma-separated list of afxRenewal.renewalStatus values, e.g. "RENEWAL_OFFER_ACCEPTED".
  status?: string;
  bubble_label_key?: string | null;
  title_label_key: string;
  body_label_key?: string | null;
  icon: RenewalNoticeIcon;
  // Optional per-state colors. Each falls back to a design token in the SCSS when absent, so
  // existing entries that only carry `color` keep rendering exactly as they do today.
  color?: string | null;
  border_color?: string | null;
  bubble_color?: string | null;
  button_color?: string | null;
  button_label_key?: string | null;
  button_aria_key?: string | null;
}

const ICON_NAME_BY_TYPE: Record<RenewalNoticeIcon, string> = {
  warning: 'error-warning',
  information: 'information-circle',
  success: 'checkmark-circle-small-green',
};

// Button keys are optional on a descriptor; these are what the threshold states have always used.
const DEFAULT_BUTTON_LABEL_KEY = 'Dashboard_Renew_Plan_Link_Text';
const DEFAULT_BUTTON_ARIA_KEY = 'Dashboard_Renew_Plan_Aria_Label';

// The thresholds label is authored in the CMS, so a malformed entry must be skipped rather than
// allowed to blank the whole notice.
function isValidStateDescriptor(value: unknown): value is RenewalNoticeStateDescriptor {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const descriptor = value as Partial<RenewalNoticeStateDescriptor>;
  const hasTitle = typeof descriptor.title_label_key === 'string' && descriptor.title_label_key.trim().length > 0;
  const hasIcon = descriptor.icon === 'warning' || descriptor.icon === 'information' || descriptor.icon === 'success';
  const hasMatcher = typeof descriptor.days === 'number' || (typeof descriptor.status === 'string' && descriptor.status.trim().length > 0);
  return hasTitle && hasIcon && hasMatcher;
}

// Shared normaliser for every delimited status list in this file. The `.filter(length > 0)` step
// is load-bearing, not tidiness: without it a blank CMS value yields [''], and ''.includes('')
// is true in JS, so every status would match (the class of bug behind ADO 4199066).
//
// Delimiters are NOT consistent across these keys, so every caller passes one explicitly or takes
// the comma default:
//   ','  {Context}_Renewal_Notice_Thresholds  (the `status` field inside each JSON entry)
//   ','  Global_Renewal_Blocked_Plan_Statuses
//   ';'  {Context}_Renewal_Notice_Hidden_Statuses
//   ';'  Dashboard_AutoRenewOff_Renewal_Statuses  (in AutoRenewOffNoticeService)
function splitStatusList(status: string | null | undefined, delimiter = ','): string[] {
  return (status ?? '')
    .split(delimiter)
    .map((entry) => entry.trim().toLowerCase())
    .filter((entry) => entry.length > 0);
}

@Injectable()
export class PlanRenewalService {
  private resourceService = inject(ADPResourceService);
  private loggedInUserSettings = inject(LoggedInUserSettings);

  private selectedPlanDetail = toSignal(this.loggedInUserSettings.$selectedPlanDetail, { initialValue: null });

  readonly afxRenewal = toSignal(this.loggedInUserSettings.$afxRenewal, { initialValue: null });

  private readonly contextSignal = signal<PlanRenewalContext>('Dashboard');
  readonly context = this.contextSignal.asReadonly();
  setContext(context: PlanRenewalContext): void {
    this.contextSignal.set(context);
  }

  readonly planCoreCertificate = computed(() => {
    const planDetail = this.selectedPlanDetail();
    if (!planDetail) {
      return null;
    }
    const planCoreConst = this.resourceService.getPortalLabel('Global_Core_Plan_Details_Prefix')?.Value;
    if (!planCoreConst) {
      return null;
    }
    const coreCertificate = planDetail.certificates?.find((c) => c.certificateNumber?.startsWith(planCoreConst));
    return coreCertificate ?? null;
  });

  readonly coverageEndsDays = computed(() => {
    const coreCertificate = this.planCoreCertificate();
    const planDetail = this.selectedPlanDetail();

    if (!coreCertificate || !planDetail) {
      return this.coverageMaxDays() + 1;
    }

    const remainingDays = daysUntil(planDetail.planEndDate);

    if (coreCertificate.status.toLowerCase() === AppConstants.PLAN_STATUS.Expired.toLowerCase() && remainingDays > 0) {
      return 0; // Treat expired plans as if they are at the end of their coverage period for the purpose of showing the renewal notice
    }
    return remainingDays;
  });

  readonly coverageMinDays = computed(() => {
    const settingKey = `${this.context()}_Renewal_Coverage_Min_Days`;
    let minDaysValue = this.resourceService.getPortalSetting(settingKey);
    if (!minDaysValue) {
      // Fallback to Global setting if context-specific setting is not found, as per current design
      minDaysValue = this.resourceService.getPortalSetting(`Global_Renewal_Coverage_Min_Days`);
    }
    return minDaysValue ? parseInt(minDaysValue.Value, 10) : 0;
  });

  readonly coverageMaxDays = computed(() => {
    const settingKey = `${this.context()}_Renewal_Coverage_Max_Days`;
    const maxDaysValue = this.resourceService.getPortalSetting(settingKey);
    if (maxDaysValue) {
      return parseInt(maxDaysValue.Value, 10);
    }
    // Fallback to the largest day threshold in {Context}_Renewal_Notice_Thresholds, instead of a
    // separate Global_Renewal_Coverage_Max_Days setting, so the coverage window and the notice
    // thresholds can't drift out of sync from a half-updated CMS edit.
    return getMaxThresholdDays(this.thresholdsSettingValue());
  });

  // Stored as a NEGATIVE day count (e.g. -30), matching the Coverage_Min_Days convention: -30 means
  // "30 days past coverage end". coverageEndsDays() is positive while coverage remains and goes negative
  // after it ends, so the grace window stays open while coverageEndsDays() >= gracePeriodDays() and
  // closes once coverageEndsDays() drops below it (see isGracePeriodEnded). Defaults to 0 if unset.
  readonly gracePeriodDays = computed<number>(() => {
    const gracePeriodValue = this.resourceService.getPortalSetting('Global_Renewal_Grace_Period_Days');
    const parsed = parseInt(gracePeriodValue?.Value ?? '', 10);
    return isNaN(parsed) ? 0 : parsed;
  });

  private toLongDate(date: Date): string {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString(undefined, options);
  }

  readonly coverageEndDate = computed(() => {
    const planEndDate = this.selectedPlanDetail()?.planEndDate;
    // Read the end date's calendar day straight off planEndDate so it never shifts with the viewer's
    // timezone. Only when coverageEndsDays() was deliberately overridden (expired status with days
    // left, no core certificate) does it fall back to today + days.
    if (planEndDate && this.coverageEndsDays() === daysUntil(planEndDate)) {
      return this.toLongDate(calendarDateOf(planEndDate));
    }
    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() + this.coverageEndsDays());
    return this.toLongDate(currentDate);
  });

  // Empty until the customer has an accepted offer; surfaced through labelContext so the
  // "renewal accepted" copy can render its dates from a single CMS HTML value.
  readonly renewedPlanStartDate = computed<string>(() => {
    const termStart = this.afxRenewal()?.selectedOffer?.newPolicy?.termStart;
    // termStart is a UTC-midnight instant; read its calendar day directly so US timezones don't shift it back a day.
    return termStart ? this.toLongDate(calendarDateOf(termStart)) : '';
  });

  private readonly thresholdsSettingValue = computed<string>(() => {
    const settingKey = `${this.context()}_Renewal_Notice_Thresholds`;
    return this.resourceService.getPortalLabel(settingKey)?.Value;
  });

  // AFX renewalStatus values for which the notice is suppressed outright, whatever the
  // thresholds label would otherwise render. Authored per context with a Global fallback, the
  // same shape as coverageMinDays above.
  //
  // NOTE the delimiter asymmetry: this setting is SEMICOLON-separated, while the `status` field
  // inside each {Context}_Renewal_Notice_Thresholds entry and Global_Renewal_Blocked_Plan_Statuses
  // are COMMA-separated. See the splitStatusList header for the full table — an author editing
  // more than one of these in a sitting will otherwise get it wrong.
  //
  // Unset, blank, or separator-only all yield an empty list, and an empty list never matches —
  // so no CMS key is required and the default is "hide nothing".
  //
  // The context list is the UNION of the Global list and the per-context one, NOT an override.
  // That is a correctness requirement, not a convenience: route access is decided by the Global
  // list alone (see globallyHiddenRenewalStatuses), so if a context could subtract from Global we
  // could render a banner whose "View my renewal" CTA the guard then refuses — a dead-end button.
  // Union makes "banner visible ⇒ route reachable" hold structurally. Authors widen suppression
  // per surface by adding to the context key; to hide on one surface only, leave Global empty.
  private readonly hiddenRenewalStatuses = computed<string[]>(() => {
    const contextValue = this.resourceService.getPortalSetting(`${this.context()}_Renewal_Notice_Hidden_Statuses`, true)?.Value;
    return [...new Set([...this.globallyHiddenRenewalStatuses(), ...splitStatusList(contextValue, ';')])];
  });

  // Route blocking reads the Global key ONLY — never the per-context one. The /renew route
  // provides its own PlanRenewalService instance and cannot call setContext() before the guard
  // runs (canActivate precedes component construction), so that instance is permanently on the
  // 'Dashboard' default. Resolving route access against a per-context key would therefore let
  // `Dashboard_..._Hidden_Statuses` bounce a user who clicked "View my renewal" from the My Plan
  // banner — a visible button that navigates nowhere. Keep display config and access config apart.
  // `true` second arg = byPassServiceLogNull: these keys are optional by design, so a miss must not
  // spam "not found in the Portal Settings" on every re-evaluation of the computed.
  private readonly globallyHiddenRenewalStatuses = computed<string[]>(() => splitStatusList(this.resourceService.getPortalSetting('Global_Renewal_Notice_Hidden_Statuses', true)?.Value, ';'));

  private readonly normalizedRenewalStatus = computed<string>(() => this.afxRenewal()?.renewalStatus?.trim().toLowerCase() ?? '');

  // True when the current AFX status is on the resolved suppression list for this context
  // (Global ∪ per-context). An absent status can never be hidden, because empty entries are
  // filtered out of the list.
  readonly isHiddenByStatus = computed<boolean>(() => {
    const renewalStatus = this.normalizedRenewalStatus();
    return renewalStatus.length > 0 && this.hiddenRenewalStatuses().includes(renewalStatus);
  });

  // As isHiddenByStatus, but against the Global-only list — see globallyHiddenRenewalStatuses.
  readonly isHiddenFromRenewalPortal = computed<boolean>(() => {
    const renewalStatus = this.normalizedRenewalStatus();
    return renewalStatus.length > 0 && this.globallyHiddenRenewalStatuses().includes(renewalStatus);
  });

  private readonly closestThresholdFallbackEnabled = computed(() => this.resourceService.isPortalSettingTrue('Global_Renewal_Notice_Closest_Threshold_Enabled'));

  private readonly noticeStates = computed<RenewalNoticeStateDescriptor[]>(() => {
    const settingValue = this.thresholdsSettingValue();
    if (!settingValue) {
      return [];
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(settingValue);
    } catch {
      return [];
    }
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.filter(isValidStateDescriptor);
  });

  private readonly statusMatchedState = computed<RenewalNoticeStateDescriptor | null>(() => {
    const renewalStatus = this.afxRenewal()?.renewalStatus?.trim().toLowerCase();
    if (!renewalStatus) {
      return null;
    }
    return this.noticeStates().find((state) => splitStatusList(state.status).includes(renewalStatus)) ?? null;
  });

  private readonly thresholdMatchedState = computed<RenewalNoticeStateDescriptor | null>(() => {
    const thresholds = this.noticeStates()
      .filter((state): state is RenewalNoticeStateDescriptor & { days: number } => typeof state.days === 'number')
      .sort((a, b) => a.days - b.days);

    const matched = thresholds.find((t) => this.coverageEndsDays() <= t.days) ?? null;
    if (matched !== null) {
      return matched;
    }
    if (this.closestThresholdFallbackEnabled() && thresholds.length > 0) {
      return thresholds[thresholds.length - 1];
    }
    return null;
  });

  // A status match wins over a day threshold: once the customer has acted on their offer, the card
  // reflects that no matter how much coverage is left. Which contexts get which states is data, not
  // code — MyPlan_Renewal_Notice_Thresholds simply carries no status entries, so My Plan keeps
  // falling through to its day thresholds.
  readonly matchedState = computed<RenewalNoticeStateDescriptor | null>(() => this.statusMatchedState() ?? this.thresholdMatchedState());

  readonly bubbleLabelKey = computed<string | null>(() => this.matchedState()?.bubble_label_key ?? null);
  readonly titleLabelKey = computed<string>(() => this.matchedState()?.title_label_key ?? '');
  readonly bodyLabelKey = computed<string | null>(() => this.matchedState()?.body_label_key ?? null);
  readonly iconType = computed<RenewalNoticeIcon>(() => this.matchedState()?.icon ?? 'information');
  readonly iconName = computed<string>(() => ICON_NAME_BY_TYPE[this.iconType()]);
  readonly iconColor = computed<string | null>(() => this.matchedState()?.color ?? null);
  readonly borderColor = computed<string | null>(() => this.matchedState()?.border_color ?? null);
  readonly bubbleColor = computed<string | null>(() => this.matchedState()?.bubble_color ?? null);
  readonly buttonColor = computed<string | null>(() => this.matchedState()?.button_color ?? null);
  readonly buttonLabelKey = computed<string>(() => this.matchedState()?.button_label_key || DEFAULT_BUTTON_LABEL_KEY);
  readonly buttonAriaKey = computed<string>(() => this.matchedState()?.button_aria_key || DEFAULT_BUTTON_ARIA_KEY);

  readonly protectionPlanLabel = computed<string>(() => {
    const planType = this.selectedPlanDetail()?.planType;
    if (!planType) {
      return '';
    }
    const key = `MyPlan_${planType}_Protection_Label`;
    return this.resourceService.isViewLabelAvailable(key) ? this.resourceService.getViewLabel(key) : '';
  });

  readonly labelContext = computed(() => ({
    coverageEndDate: this.coverageEndDate(),
    renewedPlanStartDate: this.renewedPlanStartDate(),
    days: Math.abs(this.coverageEndsDays()),
    planName: this.protectionPlanLabel(),
  }));

  readonly isExpired = computed(() => this.coverageEndsDays() < 0);

  // True once coverage is further past its end date than the (negative) grace window allows —
  // e.g. coverageEndsDays() of -31 < gracePeriodDays() of -30. See gracePeriodDays for the convention.
  readonly isGracePeriodEnded = computed(() => this.coverageEndsDays() < this.gracePeriodDays());

  readonly isNoticeVisible = computed(() => {
    if (this.isHiddenByStatus() || !this.isRenewPlanEnabled()) {
      return false;
    }

    const days = this.coverageEndsDays();
    return days < this.coverageMaxDays() && days > this.coverageMinDays();
  });

  readonly isRenewPlanEnabled = computed(() => this.afxRenewal()?.isOfferAvailable ?? false);

  // Gates entry to the /renew embedded AFX portal (see PlanRenewalActivateGuard).
  // isRenewPlanEnabled() alone is NOT enough here: once a user has accepted their offer,
  // isOfferAvailable flips to false, but they still need to be able to click back into the
  // portal to VIEW the renewal they already accepted (the accepted state's "View my renewal"
  // button) — that's a distinct entry path from "there's a new offer to act on".
  // Suppression is checked here rather than in the guard body so PlanRenewalActivateGuard needs no
  // change and its existing spec, which stubs the service with createSpyObj(['canEnterRenewalPortal']),
  // keeps passing. Note this reads the GLOBAL hidden-status list only — see
  // globallyHiddenRenewalStatuses for why per-context would create a dead-end CTA. Fails OPEN: if
  // site resources failed to load, the list is empty and entry is allowed, matching the
  // "missing CMS key preserves today's behavior" rule used throughout this service.
  readonly canEnterRenewalPortal = computed(() => !this.isHiddenFromRenewalPortal() && (this.isRenewPlanEnabled() || this.afxRenewal()?.renewalStatus === RenewalStatus.RenewalOfferAccepted));

  // Raw data-state check, context-agnostic. The notice itself no longer reads this — which state it
  // renders is decided by matchedState() against the CMS descriptors — but dashboard.component still
  // uses it to decide whether to show the notice at all.
  readonly isRenewalOfferAccepted = computed(() => this.afxRenewal()?.renewalStatus === RenewalStatus.RenewalOfferAccepted);

  readonly isRenewalVisible = computed(() => {
    if (this.isHiddenByStatus() || !this.isRenewPlanEnabled()) {
      return false;
    }

    const days = this.coverageEndsDays();
    return days < this.coverageMaxDays();
  });

  private readonly blockedPlanStatuses = computed<string[]>(() => splitStatusList(this.resourceService.getPortalSetting('Global_Renewal_Blocked_Plan_Statuses')?.Value, ','));

  readonly isValidPlanDetail = computed(() => {
    const coreCertificate = this.planCoreCertificate();
    if (!coreCertificate) {
      return false;
    }
    return !this.blockedPlanStatuses().includes(coreCertificate?.status?.toLowerCase());
  });

  readonly isGSSPModalVisible = computed(() => {
    if (!this.isRenewPlanEnabled()) {
      return false;
    }

    if (!this.isValidPlanDetail()) {
      return false;
    }

    return this.isExpired();
  });
}
